import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { CourseQaIssueTable } from './CourseQaIssueTable';
import {
  Play, FileDown, Copy, ChevronDown, ChevronRight, ExternalLink, Loader2, Check, History,
  UserCheck, AlertCircle, Info, MonitorPlay, ImageOff, Bug, PhoneCall, FileText, Database, Settings2,
  ArrowLeft, ListChecks, Crosshair
} from 'lucide-react';

const when = v => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v || '') : d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};
const roleName = r => (r === 'course_qa' ? 'Course QA reviewer' : String(r || '').replace('_', ' '));

// one cut-out of the full-page screenshot with the problem outlined in red
const ShotSpot = ({ src, box, pageW }) => {
  const W = pageW || 1440;
  const pad = 40;
  const cw = Math.min(W, Math.max(box.w + pad * 2, 640));
  const ch = Math.min(Math.max(box.h + pad * 2, 200), 560);
  const x0 = Math.max(0, Math.min(W - cw, box.x + box.w / 2 - cw / 2));
  const y0 = Math.max(0, box.y - pad);
  const pct = (v, of) => `${(v / of) * 100}%`;
  return (
    <div className="rounded border border-slate-200 overflow-hidden" data-testid="qa-shot-spot">
    <div className="relative w-full overflow-hidden bg-slate-100" style={{ aspectRatio: `${cw} / ${ch}` }}>
      <img src={src} alt={`Where on the page: ${box.label || ''}`} className="absolute"
        style={{ maxWidth: 'none', width: pct(W, cw), left: `-${pct(x0, cw)}`, top: `-${pct(y0, ch)}` }} />
      <div className="absolute border-2 border-rose-500 rounded-sm shadow-[0_0_0_9999px_rgba(15,23,42,0.18)]"
        style={{ left: pct(box.x - x0, cw), top: pct(box.y - y0, ch), width: pct(box.w, cw), height: pct(Math.min(box.h, ch - pad), ch) }} />
    </div>
    </div>
  );
};

const SEV_CHIP = {
  critical: 'bg-rose-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-400 text-amber-950',
  low: 'bg-slate-300 text-slate-700',
};
const SEV_TEXT = { critical: 'text-rose-600', high: 'text-orange-500', medium: 'text-amber-500', low: 'text-slate-400' };
const SECTION_PILL = {
  pass: 'bg-emerald-100 text-emerald-700',
  fail: 'bg-rose-100 text-rose-700',
  warning: 'bg-amber-100 text-amber-700',
  hidden: 'bg-slate-200 text-slate-600',
  absent: 'bg-slate-200 text-slate-600',
};
const SECTION_WORD = { pass: 'Pass', fail: 'Fail', warning: 'Warning', hidden: 'Hidden', absent: 'Absent' };
const CHECK_WORD = { pass: 'Pass', fail: 'Fail', nv: 'Not verifiable', na: 'Not applicable' };
const FINDING_STATES = [
  { v: 'open', label: 'Open' },
  { v: 'in_progress', label: 'Being fixed' },
  { v: 'fixed', label: 'Fixed' },
  { v: 'accepted', label: 'Accepted as-is' },
];
const RESOLVED = ['fixed', 'accepted'];
const STEPS = ['Fetching course', 'Checking 16 sections', 'Verifying links', 'Finalising report'];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mt-5 flex-wrap" data-testid="qa-progress-steps">
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${i <= step ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
            {i < step ? <Check className="w-3 h-3" /> : i + 1}
          </span>
          <span className={`text-xs ${i <= step ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>{label}</span>
        </div>
        {i < STEPS.length - 1 && <span className="h-px w-10 bg-slate-200 hidden sm:block" />}
      </React.Fragment>
    ))}
  </div>
);

export default function CourseQaPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [input, setInput] = useState(params.get('slug') || '');
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [openFinding, setOpenFinding] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  const [expandAll, setExpandAll] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState('');
  const [view, setView] = useState('report');
  const [showResolved, setShowResolved] = useState(true);
  const [layout, setLayoutState] = useState(() => localStorage.getItem('qa-issue-layout') || 'cards');
  const setLayout = v => { setLayoutState(v); localStorage.setItem('qa-issue-layout', v); };
  const [showCurriculum, setShowCurriculum] = useState(false);
  const [history, setHistory] = useState([]);
  const [shotUrl, setShotUrl] = useState('');
  const [settings, setSettings] = useState(null);

  const loadHistory = useCallback(slug => {
    api.get('/course-qa/audits', { params: { slug } }).then(r => setHistory(r.data.audits)).catch(() => {});
  }, []);
  const refresh = useCallback(id => api.get(`/course-qa/audit/${id}`).then(r => setReport(r.data)).catch(() => {}), []);

  const openAudit = useCallback(id => {
    api.get(`/course-qa/audit/${id}`)
      .then(r => { setReport(r.data); setParams({ audit: id }); setInput(r.data.slug); loadHistory(r.data.slug); setView('report');
        setTimeout(() => document.getElementById('qa-issues-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80); })
      .catch(() => toast.error('Could not open that audit'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAudit = async (value) => {
    const slug = (value ?? input).trim();
    if (!slug) return toast.error('Paste a course URL or slug');
    setRunning(true); setStep(0);
    const ticker = setInterval(() => setStep(s => Math.min(s + 1, STEPS.length - 1)), 2500);
    try {
      const r = await api.post('/course-qa/audit', { slug });
      setReport(r.data);
      refresh(r.data.id);
      setParams({ audit: r.data.id });
      loadHistory(r.data.slug);
      setView('report');
      toast.success(`Audited ${r.data.title}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
    } finally { clearInterval(ticker); setRunning(false); setStep(0); }
  };

  useEffect(() => {
    if (report?.render_status !== 'running') return undefined;
    const t = setInterval(() => refresh(report.id), 5000);
    return () => clearInterval(t);
  }, [report?.render_status, report?.id, refresh]);

  useEffect(() => {
    if (!report?.render?.checked_at) { setShotUrl(''); return undefined; }
    let url = '';
    api.get(`/course-qa/audit/${report.id}/screenshot`, { responseType: 'blob' })
      .then(r => { url = URL.createObjectURL(r.data); setShotUrl(url); })
      .catch(() => setShotUrl(''));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [report?.render?.checked_at, report?.id]);

  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    api.get('/course-qa/settings').then(r => setSettings(r.data)).catch(() => {});
    const id = params.get('audit');
    const slug = params.get('slug');
    if (id) { openAudit(id); return; }
    if (slug) {
      api.get('/course-qa/audits', { params: { slug } })
        .then(r => { const last = (r.data.audits || [])[0]; if (last) openAudit(last.id); else runAudit(slug); })
        .catch(() => runAudit(slug));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runRender = async () => {
    try {
      await api.post(`/course-qa/audit/${report.id}/render`);
      setReport(p => ({ ...p, render_status: 'running' }));
      toast.success('Opening the live page in a browser — this takes about half a minute');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start the live page check'); }
  };

  const tick = async (key, checked, withNote) => {
    try {
      const keep = (report.checklist || {})[key]?.note || '';
      await api.post(`/course-qa/audit/${report.id}/checklist`, { rule: key, checked, note: withNote ?? keep });
      refresh(report.id);
    } catch { toast.error('Could not save that change'); }
  };

  const setFindingState = async (fid, state) => {
    try {
      await api.post(`/course-qa/audit/${report.id}/finding`, { rule: fid, state });
      await refresh(report.id);
      if (RESOLVED.includes(state)) {
        setShowResolved(true);
        toast.success(state === 'fixed' ? 'Marked as fixed — now under "Resolved" below' : 'Accepted as-is — now under "Resolved" below', {
          action: { label: 'Show me', onClick: () => goResolved() },
        });
      } else {
        toast.success(`Issue set to ${FINDING_STATES.find(x => x.v === state)?.label}`);
      }
    } catch { toast.error('Could not update that issue'); }
  };

  const goResolved = () => {
    setView('report'); setShowResolved(true);
    setTimeout(() => document.getElementById('qa-resolved-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  const showDetails = key => {
    setLayout('cards'); setOpenFinding(key); setShowResolved(true);
    setTimeout(() => document.querySelector(`[data-testid="qa-finding-${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
  };
  const backToIssues = () => {
    setView('report');
    setTimeout(() => document.getElementById('qa-issues-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const updateSetting = async (page_path) => {
    try {
      const r = await api.put('/course-qa/settings', { page_path });
      setSettings(s => ({ ...s, ...r.data }));
      toast.success('Course page address updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save that'); }
  };

  const copyMarkdown = async () => {
    try {
      const r = await api.get(`/course-qa/audit/${report.id}/markdown`);
      await navigator.clipboard.writeText(r.data.markdown);
      toast.success('Report copied as Markdown');
    } catch { toast.error('Copy failed'); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/course-qa/audit/${report.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `CourseQA-${report.slug}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error('PDF failed'); }
  };

  const states = report?.findings_state || {};
  const stateOf = f => (states[String(f.fid || f.rule)] || {});
  const allFindings = report?.findings || [];
  const isResolved = f => RESOLVED.includes(stateOf(f).state);
  const openIssues = allFindings.filter(f => !isResolved(f));
  const resolvedIssues = allFindings.filter(isResolved);
  const pageIssues = openIssues.filter(f => f.visibility !== 'record');
  const recordIssues = openIssues.filter(f => f.visibility === 'record');
  const groups = report?.review_groups || [];
  const checklist = report?.checklist || {};
  const totalChecks = groups.reduce((n, g) => n + g.checks.length, 0);
  const doneChecks = groups.reduce((n, g) => n + g.checks.filter(c => checklist[c.key]?.checked).length, 0);
  const openCounts = report?.open_counts || {};
  const curriculum = report?.curriculum || {};

  const navTab = (to, label) => (
    <button key={to} onClick={() => navigate(to)}
      className={`text-xs font-medium px-2.5 py-1 rounded-sm transition-colors ${location.pathname === to ? 'text-white' : 'text-slate-300 hover:text-white'}`}
      data-testid={`qa-nav-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</button>
  );

  const IssueCard = ({ f, resolved }) => {
    const key = String(f.fid || f.rule);
    const open = openFinding === key;
    const st = stateOf(f);
    return (
      <div className={`bg-white border rounded-md overflow-hidden ${resolved ? 'border-slate-200 opacity-75' : 'border-slate-200'}`}
        data-testid={`qa-finding-${key}`}>
        <button onClick={() => setOpenFinding(open ? null : key)} className="w-full text-left p-4 flex gap-4">
          <div className="w-20 shrink-0">
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${resolved ? 'bg-emerald-100 text-emerald-700' : SEV_CHIP[f.severity]}`}>
              {resolved ? (st.state === 'accepted' ? 'Accepted' : 'Fixed') : f.severity}
            </span>
            <div className="text-[11px] text-slate-400 mt-1.5">{f.section}</div>
            {f.section_type && <div className="text-[9px] font-mono text-slate-400 mt-0.5" title="CMS section code">{f.section_type}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-bold ${resolved ? 'text-slate-500 line-through decoration-slate-300' : 'text-slate-800'}`}>
              {f.issue || f.name}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{f.name} <span className="text-[10px] text-slate-400">· Rule {f.rule}</span></p>
            {resolved && st.by && (
              <p className="text-[11px] text-emerald-700 mt-1" data-testid={`qa-resolved-by-${key}`}>
                {st.state === 'accepted' ? 'Accepted as-is' : 'Marked fixed'} by {st.by} on {when(st.at)}
                {st.note ? ` · “${st.note}”` : ''} · open it to reopen if needed
              </p>
            )}
            {!resolved && st.state === 'in_progress' && (
              <span className="inline-block mt-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                Being fixed · {st.by}
              </span>
            )}
          </div>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        </button>
        {open && (
          <div className="px-4 pb-4 sm:pl-24 space-y-2 min-w-0">
            {f.actual && (
              <pre className="text-[11px] font-mono text-slate-600 bg-slate-50 border border-slate-100 rounded p-3 whitespace-pre-wrap break-all">{f.actual}</pre>
            )}
            {f.verify?.status && <p className="text-[10px] text-slate-400 break-all">Link check: HTTP {f.verify.status} · {f.verify.content_type || 'unknown type'}{f.verify.final_url ? ` · ${f.verify.final_url}` : ''}</p>}
            {f.verify?.reason && <p className="text-[10px] text-slate-400">Link check: {f.verify.reason}</p>}
            {f.fix && <p className="text-xs text-slate-700"><span className="font-bold">Fix:</span> {f.fix}</p>}
            {f.category !== 'F' && (
              <div className="pt-1" data-testid={`qa-finding-section-spot-${key}`}>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1 mb-1.5">
                  <Crosshair className="w-3 h-3" /> Where on the page
                </div>
                {f.visibility === 'record' ? (
                  <p className="text-[11px] text-slate-400">This is a CMS record setting — it is not shown on the page.</p>
                ) : report.render?.section_boxes?.[f.section_type] && shotUrl ? (
                  <div className="max-w-xl">
                    <ShotSpot src={shotUrl} box={report.render.section_boxes[f.section_type]} pageW={report.render?.page?.w} />
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {f.section} section, about {Math.round(report.render.section_boxes[f.section_type].y / 100) * 100}px down the page
                    </p>
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-400">
                    {!report.render ? 'Click "Check live page" above to see this section on a screenshot.'
                      : !f.section_type ? 'This check covers several parts of the page, so there is no single spot to show.'
                      : (report.sections || []).some(x => x.type === f.section_type && (x.status === 'hidden' || x.enabled === false))
                        ? `The ${f.section} section is switched off in the CMS, so it is not on the page.`
                      : 'This section could not be found on the live page screenshot.'}
                  </p>
                )}
              </div>
            )}
            <button onClick={() => navigate(`/course-qa/rules#rule-${f.rule}`)} className="text-[11px] text-blue-600 hover:underline"
              data-testid={`qa-finding-rule-help-${key}`}>What does this rule check?</button>
            {f.category === 'F' && (
              <div className="pt-1" data-testid={`qa-finding-spots-${key}`}>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1 mb-1.5">
                  <Crosshair className="w-3 h-3" /> Where on the page
                </div>
                {(f.boxes || []).length > 0 && shotUrl ? (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {f.boxes.map((b, i) => (
                      <div key={i}>
                        <ShotSpot src={shotUrl} box={b} pageW={report.render?.page?.w} />
                        <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                          {b.label ? `“${b.label}” · ` : ''}about {Math.round(b.y / 100) * 100}px down the page
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-400">
                    {f.rule === 'R2' && !(f.boxes || []).length ? 'The section is missing from the page entirely, so there is nothing to point at on the screenshot.'
                      : f.rule === 'R3' ? 'Browser errors happen behind the scenes and are not visible on the screenshot.'
                      : 'Run "Re-check live page" to mark this spot on the screenshot.'}
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              {FINDING_STATES.map(s => (
                <button key={s.v} onClick={() => setFindingState(key, s.v)}
                  data-testid={`qa-finding-state-${key}-${s.v}`}
                  className={`text-[10px] font-semibold px-2 py-1 rounded border transition-colors ${(st.state || 'open') === s.v ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                  {s.label}
                </button>
              ))}
              {st.at && <span className="text-[10px] text-slate-400">last changed by {st.by} on {when(st.at)}</span>}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="-m-4 sm:-m-6" data-testid="course-qa-page">
      <div className="bg-[#0f2b4c] px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="text-white text-base sm:text-lg font-bold tracking-tight">Course Page QA</span>
          <span className="text-slate-300 text-xs hidden sm:inline">Content auditor for Training Basket</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {navTab('/course-qa', 'Audit a page')}
          {navTab('/course-qa/all', 'Audit all courses')}
          {navTab('/course-qa/defects', 'Defect board')}
          {navTab('/course-qa/rules', 'Rules guide')}
          {user?.role !== 'course_qa' && navTab('/course-qa/management', 'Management')}
        </div>
      </div>

      <div className="p-4 sm:p-6 bg-slate-50 min-h-[calc(100vh-7rem)] space-y-5">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="grid lg:grid-cols-[1fr,280px] gap-6">
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-slate-800 mb-2">1. Enter a course URL or slug</h2>
              <div className="flex gap-2 flex-wrap min-w-0">
                <Input value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && runAudit()}
                  placeholder="https://edu-funnel-1.emergent.host/courses/ccna-training-noida"
                  className="flex-1 min-w-0 rounded-md" data-testid="qa-slug-input" />
                <Button onClick={() => runAudit()} disabled={running}
                  className="rounded-md bg-blue-600 hover:bg-blue-700 px-6" data-testid="qa-run-audit">
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  {running ? 'Auditing…' : 'Audit page'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">
                Full URL or just the slug. Both /courses/slug and /slug are accepted
                {settings?.detected ? ` — pages are currently served at ${settings.detected}` : ''}.
              </p>
              {running && <Stepper step={step} />}
            </div>
            <div className="border-l border-slate-100 lg:pl-6">
              <h3 className="text-sm font-bold text-slate-800">Or audit all courses</h3>
              <p className="text-[11px] text-slate-400 mt-1 mb-3">Check every course and see error counts for each one.</p>
              <Button variant="outline" onClick={() => navigate('/course-qa/all')}
                className="w-full rounded-md border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="qa-goto-all">
                Audit all courses
              </Button>
              {user?.role !== 'course_qa' && settings && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 flex items-center gap-1">
                    <Settings2 className="w-3 h-3" /> Course page address
                  </label>
                  <select value={settings.page_path} onChange={e => updateSetting(e.target.value)}
                    className="mt-1 w-full h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                    data-testid="qa-page-path">
                    <option value="auto">Detect automatically</option>
                    <option value="/courses/{slug}">/courses/slug</option>
                    <option value="/{slug}">/slug</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {report && (
          <div className="grid lg:grid-cols-[1fr,380px] gap-5 items-start">
            <div className="space-y-5 min-w-0">
              <div className="bg-white border border-slate-200 rounded-md p-5">
                <div className="flex gap-4">
                  {report.image && (
                    <img src={report.image} alt="" className="w-36 h-24 object-cover rounded-md border border-slate-200 hidden sm:block" />
                  )}
                  <div className="min-w-0 flex-1">
                    <h1 className="text-2xl font-bold text-slate-800 leading-tight" data-testid="qa-course-title">{report.title}</h1>
                    <p className="text-xs text-slate-500 mt-1">
                      Slug: {report.slug}
                      {report.partner ? <span>  ·  Partner: {report.partner}</span> : null}
                      {report.updated_at ? <span>  ·  Last updated: {String(report.updated_at).slice(0, 10)}</span> : null}
                      <span>  ·  Page status: <span className="capitalize">{report.page_status || 'not set'}</span></span>
                    </p>
                    <a href={report.live_url} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1">
                      View live page <ExternalLink className="w-3 h-3" />
                    </a>
                    <div className="mt-2">
                      <Button size="sm" variant="outline" onClick={runRender}
                        disabled={report.render_status === 'running'}
                        className="rounded-md h-7 text-xs border-slate-300" data-testid="qa-render-check">
                        {report.render_status === 'running'
                          ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Checking live page…</>
                          : <><MonitorPlay className="w-3.5 h-3.5 mr-1.5" /> {report.render ? 'Re-check live page' : 'Check live page'}</>}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1.5">
                      Audited by {report.audited_by} · {String(report.audited_at).slice(0, 19).replace('T', ' ')} UTC ·
                      Report {String(report.id).slice(0, 8).toUpperCase()}
                      {report.carried_from ? ' · reviewer responses carried over from the previous run' : ''}
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/70 px-4 py-3 flex items-center gap-5 flex-wrap"
                  data-testid="qa-severity-band">
                  <div>
                    <div className={`text-3xl font-bold ${report.open_total ? 'text-slate-800' : 'text-emerald-600'}`} data-testid="qa-open-total">
                      {report.open_total ?? 0}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">open issues</div>
                  </div>
                  <div className="h-10 w-px bg-slate-200" />
                  <div className="flex items-center gap-3 flex-wrap">
                    {['critical', 'high', 'medium', 'low'].map(s => (
                      <span key={s} className={`text-sm font-bold ${openCounts[s] ? SEV_TEXT[s] : 'text-slate-300'}`} data-testid={`qa-count-${s}`}>
                        {openCounts[s] || 0} <span className="font-semibold text-xs">{s}</span>
                      </span>
                    ))}
                  </div>
                  <div className="ml-auto text-right">
                    <button onClick={goResolved} disabled={!report.resolved_total}
                      className="text-xs text-slate-500 enabled:hover:text-emerald-700 enabled:hover:underline" data-testid="qa-resolved-total">
                      {report.resolved_total || 0} resolved of {report.found_total || 0} found{report.resolved_total ? ' — view' : ''}
                    </button>
                    <div className="h-1.5 w-40 bg-slate-200 rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${report.found_total ? ((report.resolved_total || 0) / report.found_total) * 100 : 0}%` }} />
                    </div>
                  </div>
                </div>

                {(report.data_notes || []).length > 0 && (
                  <div className="mt-3 border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="qa-data-notes">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                      <Info className="w-3.5 h-3.5" /> Record notes (not scored)
                    </div>
                    {report.data_notes.map((n, i) => <p key={i} className="text-[11px] text-amber-800 mt-1">{n}</p>)}
                  </div>
                )}
                {report.render_error && (
                  <p className="text-[11px] text-rose-700 mt-2" data-testid="qa-render-error">
                    The live page check failed: {report.render_error}
                  </p>
                )}
                {report.render?.checked_at && (
                  <div className="mt-3 border border-slate-200 rounded-md overflow-hidden" data-testid="qa-render-panel">
                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                        <MonitorPlay className="w-3.5 h-3.5" /> What a visitor actually sees
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {report.render.images} images · {report.render.blocks ?? report.render.sections} page blocks · checked by {report.render.checked_by} on {String(report.render.checked_at).slice(0, 16).replace('T', ' ')} UTC{report.render.carried_from ? ' (kept from the previous audit — re-check for fresh results)' : ''}
                      </span>
                    </div>
                    <div className="p-3 grid sm:grid-cols-[200px,1fr] gap-3">
                      {shotUrl
                        ? <a href={shotUrl} target="_blank" rel="noreferrer" data-testid="qa-render-screenshot" className="block group/shot">
                            <span className="block h-40 overflow-hidden rounded border border-slate-200">
                              <img src={shotUrl} alt="Full live page screenshot" className="w-full object-cover object-top" />
                            </span>
                            <span className="text-[10px] text-blue-600">Open the full-page screenshot</span>
                          </a>
                        : <div className="w-full h-28 bg-slate-100 rounded animate-pulse" />}
                      <div className="space-y-1.5 text-[11px] min-w-0">
                        <div className="flex items-start gap-1.5">
                          <ImageOff className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                          <span className={report.render.broken.length ? 'text-rose-700' : 'text-slate-500'}>
                            {report.render.broken.length ? `${report.render.broken.length} image(s) fail to load in the browser` : 'All images load in the browser'}
                          </span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <Bug className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                          <span className={report.render.console_errors.length || report.render.failed_requests.length ? 'text-amber-700' : 'text-slate-500'}>
                            {report.render.console_errors.length || report.render.failed_requests.length
                              ? `${report.render.console_errors.length + report.render.failed_requests.length} browser error(s)`
                              : 'No errors thrown in the browser'}
                          </span>
                        </div>
                        <div className="flex items-start gap-1.5">
                          <PhoneCall className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                          <span className={report.render.phones.length > 1 ? 'text-amber-700' : 'text-slate-500'}>
                            Phone numbers: {report.render.phones.join(', ') || 'none'}
                          </span>
                        </div>
                        {report.render.prices?.length > 0 && (
                          <p className="text-slate-500 break-words">Prices on the page: {report.render.prices.join(', ')}</p>
                        )}
                        {report.render.blank_bands?.length > 0 && (
                          <p className="text-rose-700">{report.render.blank_bands.length} empty section band(s) on the page</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {!report.complete && (
                  <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {(report.not_verifiable || []).length} check(s) could not be verified from the course record and are not counted as passed.
                  </p>
                )}
              </div>

              {/* curriculum review */}
              {curriculum.module_count > 0 && (
                <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="qa-curriculum">
                  <button onClick={() => setShowCurriculum(!showCurriculum)} className="w-full px-4 py-3 flex items-center gap-3 text-left">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-bold text-slate-800">Curriculum review</span>
                    <span className="text-xs text-slate-400">{curriculum.module_count} modules · {curriculum.topic_count} topics</span>
                    {curriculum.syllabus_pdf
                      ? <a href={curriculum.syllabus_pdf} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                          className="ml-auto text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="qa-syllabus-preview">
                          Preview syllabus PDF <ExternalLink className="w-3 h-3" />
                        </a>
                      : <span className="ml-auto text-xs text-rose-600">No syllabus PDF on the record</span>}
                    {showCurriculum ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                  {showCurriculum && (
                    <div className="px-4 pb-4 space-y-3">
                      {curriculum.modules.map((m, i) => (
                        <div key={i} className="border-t border-slate-100 pt-2">
                          <div className="text-xs font-bold text-slate-700">{m.name || `Module ${i + 1}`}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{m.topics.join(' · ') || 'No topics listed'}</div>
                          {m.tools?.length > 0 && <div className="text-[10px] text-slate-400 mt-0.5">Tools: {m.tools.join(', ')}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div id="qa-issues-anchor" className="scroll-mt-4">
                <div className="flex items-end justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">
                      {view === 'history' ? 'Past audits of this course' : view === 'activity' ? 'What the team did on this audit'
                        : layout === 'table' ? `All issues (${openIssues.length} open · ${resolvedIssues.length} resolved)` : `Issues on the page (${pageIssues.length})`}
                    </h2>
                    <p className="text-xs text-slate-400">
                      {view === 'history' ? 'Every time this course was audited, newest first. Pick one to open it.'
                        : view === 'activity' ? 'Every change, newest first, with who made it and when.'
                        : 'Open issues, most severe first. Fixed or Accepted issues move to "Resolved" at the bottom of this list.'}
                    </p>
                  </div>
                  <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="qa-view-tabs">
                    {[['report', 'Issues', ListChecks], ['history', 'Past audits', History], ['activity', 'Activity', UserCheck]].map(([v, label, Icon]) => (
                      <button key={v} onClick={() => (v === 'report' ? backToIssues() : setView(v))}
                        className={`text-xs px-2.5 py-1 rounded inline-flex items-center gap-1 transition-colors ${view === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                        data-testid={`qa-tab-${v === 'report' ? 'issues' : v}`}>
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </button>
                    ))}
                  </div>
                </div>

                {view === 'report' && (
                  <div className="mt-3 flex items-center gap-2" data-testid="qa-layout-toggle">
                    <span className="text-[11px] text-slate-400">Show as</span>
                    {[['cards', 'Cards'], ['table', 'Table']].map(([v, l]) => (
                      <button key={v} onClick={() => setLayout(v)} data-testid={`qa-layout-${v}`}
                        className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${layout === v ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>{l}</button>
                    ))}
                  </div>
                )}
                {view === 'report' && layout === 'table' && (
                  <div className="mt-3">
                    <CourseQaIssueTable findings={[...openIssues, ...resolvedIssues]} stateOf={stateOf}
                      onState={setFindingState} onDetails={showDetails} />
                  </div>
                )}

                {view !== 'report' && (
                  <button onClick={backToIssues} className="mt-3 text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="qa-back-to-issues">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to issues ({openIssues.length} open)
                  </button>
                )}

                {view === 'history' && (
                  <div className="mt-3 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="qa-history">
                    {history.length === 0 && <div className="p-4 text-xs text-slate-400">No earlier audits for this course.</div>}
                    {history.map(h => (
                      <button key={h.id} onClick={() => openAudit(h.id)} data-testid="qa-history-row"
                        className="w-full text-left border-b border-slate-100 last:border-0 px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs text-slate-800 font-semibold">
                            Audited on {when(h.audited_at)} by {h.audited_by}
                            {h.id === report.id && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">You are viewing this one</span>}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {(() => {
                              const c = h.counts || {};
                              const total = ['critical', 'high', 'medium', 'low'].reduce((n, s) => n + (c[s] || 0), 0);
                              const parts = ['critical', 'high', 'medium', 'low'].filter(s => c[s]).map(s => `${c[s]} ${s}`);
                              const ticks = Object.values(h.checklist || {}).filter(x => x.checked).length;
                              return `Found ${total} issue${total === 1 ? '' : 's'}${parts.length ? ` (${parts.join(', ')})` : ''} · ${ticks} of ${totalChecks} review checks ticked`;
                            })()}
                          </div>
                        </div>
                        <span className="text-[11px] text-blue-600 shrink-0">{h.id === report.id ? '' : 'Open'}</span>
                      </button>
                    ))}
                  </div>
                )}

                {view === 'activity' && (
                  <div className="mt-3 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="qa-activity">
                    {(report.log || []).length === 0 && <div className="p-4 text-xs text-slate-400">No activity recorded yet.</div>}
                    {(report.log || []).map(l => (
                      <div key={l.id} className="border-b border-slate-100 last:border-0 px-4 py-2.5" data-testid="qa-activity-row">
                        <p className="text-xs text-slate-700">
                          <span className="font-semibold text-slate-900">{l.user_name}</span>
                          <span className="text-slate-400"> ({roleName(l.user_role)})</span>{' '}
                          {(l.message || l.detail || '').replace(/^([A-Z])(?=[a-z])/, c => c.toLowerCase())}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{when(l.at)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {view === 'report' && layout === 'cards' && (
                  <div className="space-y-3 mt-3" data-testid="qa-findings">
                    {pageIssues.length === 0 && (
                      <div className="bg-white border border-emerald-200 rounded-md p-4 text-sm text-emerald-800">
                        Nothing open on the page. {resolvedIssues.length > 0 ? `${resolvedIssues.length} issue(s) were resolved by the team.` : ''}
                      </div>
                    )}
                    {pageIssues.map(f => <IssueCard key={String(f.fid || f.rule)} f={f} />)}

                    {recordIssues.length > 0 && (
                      <div className="pt-2">
                        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                          <Database className="w-3.5 h-3.5 text-slate-400" /> Record settings ({recordIssues.length})
                        </h3>
                        <p className="text-xs text-slate-400 mb-2">
                          These are CMS fields, not something you can see on the page — for example the publication status.
                        </p>
                        <div className="space-y-3" data-testid="qa-record-findings">
                          {recordIssues.map(f => <IssueCard key={String(f.fid || f.rule)} f={f} />)}
                        </div>
                      </div>
                    )}

                    {resolvedIssues.length > 0 && (
                      <div className="pt-2 scroll-mt-4" id="qa-resolved-anchor">
                        <button onClick={() => setShowResolved(!showResolved)}
                          className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                          data-testid="qa-toggle-resolved">
                          {showResolved ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          Resolved ({resolvedIssues.length}) — fixed or accepted as-is. Open any one to reopen it.
                        </button>
                        {showResolved && (
                          <div className="space-y-3 mt-2" data-testid="qa-resolved-findings">
                            {resolvedIssues.map(f => <IssueCard key={String(f.fid || f.rule)} f={f} resolved />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {view === 'report' && (
                <div>
                  <div className="flex items-end justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">Section by section ({(report.sections || []).length})</h2>
                      <p className="text-xs text-slate-400">Every section of the course template, in page order.</p>
                    </div>
                    <button onClick={() => setExpandAll(!expandAll)} className="text-xs text-blue-600 hover:underline" data-testid="qa-expand-all">
                      {expandAll ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>
                  <div className="mt-3 space-y-2" data-testid="qa-sections">
                    {(report.sections || []).map(sec => {
                      const open = expandAll || openSection === sec.type;
                      const mine = allFindings.filter(f => f.section_type === sec.type);
                      const left = mine.filter(f => !isResolved(f)).length;
                      const done = mine.length - left;
                      const status = sec.status === 'fail' && mine.length && !left ? 'pass' : sec.status;
                      return (
                        <div key={sec.type} className="bg-white border border-slate-200 rounded-md overflow-hidden">
                          <button onClick={() => setOpenSection(open && !expandAll ? null : sec.type)}
                            className="w-full px-3 py-2.5 flex items-center gap-3 text-left" data-testid={`qa-section-${sec.type}`}>
                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-16 text-center ${SECTION_PILL[status]}`}>
                              {mine.length && !left && sec.status === 'fail' ? 'Resolved' : SECTION_WORD[status]}
                            </span>
                            <span className="text-sm font-semibold text-slate-800">{sec.name}</span>
                            <span className="ml-auto text-xs text-slate-500 shrink-0" data-testid={`qa-section-count-${sec.type}`}>
                              {mine.length ? `${left} open${done ? ` · ${done} resolved` : ''}` : `${sec.issues || 0} issues`}
                            </span>
                            {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          </button>
                          {open && (
                            <div className="px-3 pb-3 border-t border-slate-100 pt-2 space-y-1.5 min-w-0">
                              {(sec.checks || []).length === 0 && <p className="text-[11px] text-slate-400">No automated check reports against this section.</p>}
                              {(sec.checks || []).map(c => (
                                <div key={String(c.fid || c.rule)} className="text-[11px] min-w-0">
                                  <span className="font-semibold text-slate-500">#{c.rule}</span>{' '}
                                  <span className={c.status === 'fail' ? 'text-rose-700' : 'text-slate-600'}>{c.name}</span>{' '}
                                  <span className="text-slate-400">— {CHECK_WORD[c.status] || c.status}</span>
                                  {c.actual && <div className="font-mono text-[10px] text-slate-400 truncate">{c.actual}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ───── reviewer checklist: six grouped checks ───── */}
            <div className="bg-white border border-slate-200 rounded-md p-4 lg:sticky lg:top-4" data-testid="qa-checklist">
              <div className="flex items-baseline justify-between">
                <h2 className="text-base font-bold text-slate-800">Review checklist</h2>
                <span className="text-xs text-slate-500" data-testid="qa-passed-count">{doneChecks} of {totalChecks} done</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full transition-all"
                  style={{ width: `${totalChecks ? (doneChecks / totalChecks) * 100 : 0}%` }} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5" data-testid="qa-count-signed">
                The same baseline for every course. Anyone with QA access can tick an item; every change is logged.
              </p>

              <div className="mt-4 space-y-3">
                {groups.map(g => {
                  const open = openGroups[g.id] !== false;
                  const done = g.checks.filter(c => checklist[c.key]?.checked).length;
                  const groupOpenIssues = openIssues.filter(f => f.group === g.id).length;
                  return (
                    <div key={g.id} className="border border-slate-100 rounded-md">
                      <button onClick={() => setOpenGroups(p => ({ ...p, [g.id]: open ? false : true }))}
                        className="w-full flex items-start justify-between text-left px-3 py-2 gap-2" data-testid={`qa-group-${g.id}`}>
                        <span className="min-w-0">
                          <span className="text-sm font-bold text-slate-800 block">{g.name}</span>
                          <span className="text-[10px] text-slate-400 block">{g.blurb}</span>
                          {g.sections.length > 0 && (
                            <span className="text-[10px] text-slate-400 block mt-0.5">Covers: {g.sections.join(', ')}</span>
                          )}
                        </span>
                        <span className="shrink-0 text-right">
                          <span className={`text-[10px] font-bold block ${groupOpenIssues ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {groupOpenIssues ? `${groupOpenIssues} open` : 'clear'}
                          </span>
                          <span className="text-[10px] text-slate-400">{done}/{g.checks.length}</span>
                        </span>
                      </button>
                      {open && (
                        <div className="px-3 pb-2.5 space-y-2">
                          {g.checks.map(c => {
                            const t = checklist[c.key] || {};
                            return (
                              <div key={c.key} className="group" data-testid={`qa-check-${c.key}`}>
                                <label className="flex items-start gap-2 cursor-pointer">
                                  <input type="checkbox" checked={!!t.checked}
                                    onChange={e => tick(c.key, e.target.checked)}
                                    className="mt-0.5 w-4 h-4 accent-blue-600 shrink-0" data-testid={`qa-check-box-${c.key}`} />
                                  <span className={`text-xs leading-snug ${t.checked ? 'text-slate-500 line-through decoration-slate-300' : 'text-slate-700'}`}>
                                    {c.label}
                                  </span>
                                </label>
                                {t.by && (
                                  <p className="text-[10px] text-slate-400 ml-6">
                                    {t.checked ? 'Signed off' : 'Opened'} by {t.by} · {String(t.at).slice(0, 16).replace('T', ' ')}
                                    {t.note ? ` · “${t.note}”` : ''}
                                  </p>
                                )}
                                {noteFor === c.key ? (
                                  <div className="ml-6 mt-1 flex gap-1.5">
                                    <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                                      placeholder="What did you verify?" className="rounded text-xs" data-testid={`qa-note-input-${c.key}`} />
                                    <Button size="sm" className="rounded" data-testid={`qa-note-save-${c.key}`}
                                      onClick={() => { tick(c.key, !!t.checked, note); setNoteFor(null); setNote(''); }}>Save</Button>
                                  </div>
                                ) : (
                                  <button onClick={() => { setNoteFor(c.key); setNote(t.note || ''); }}
                                    className={`text-[10px] text-blue-600 hover:underline ml-6 ${t.note ? '' : 'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity'}`}
                                    data-testid={`qa-note-${c.key}`}>
                                    {t.note ? 'Edit note' : 'Add note'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-2 mt-5">
                <Button variant="outline" onClick={copyMarkdown}
                  className="rounded-md border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="qa-copy-markdown">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy as Markdown
                </Button>
                <Button onClick={downloadPdf} className="rounded-md bg-blue-600 hover:bg-blue-700" data-testid="qa-download-pdf">
                  <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download report
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
